import type { EngineDocument, SceneNode } from "../scene/model";
import { type AffineMatrix, invertAffine } from "../geometry/affine";
import { nodeMatrix } from "../native/transforms";
import { hasEffects } from "../native/effects";
import { cornerRadius } from "../native/corners";
import { NativeCanvasRenderer } from "./native-canvas";
import { effectBounds } from "./native-effects-raster";
import type { ImageStore } from "./image-store";

/** Encoded source pixels stay independent of both document size and display zoom. */
export function regionRaster(
  document: EngineDocument,
  target: SceneNode,
  feather: number,
  images: ImageStore,
) {
  const image = images.get(
      document.assets.find((a) => a.id === target.media.image)?.source ?? "",
    ),
    canvas = globalThis.document.createElement("canvas");
  const exact =
    target.kind === "image" &&
    image &&
    target.appearance.opacity !== 0 &&
    !feather &&
    !target.layer.clipToBelow &&
    !hasEffects(target) &&
    !cornerRadius(target);
  let matrix: AffineMatrix,
    blurScale = 1;
  let dispose = () => {
    canvas.width = canvas.height = 0;
  };
  if (exact) {
    // Apply the selection in source coordinates, keeping rotation, shear and
    // fractional placement outside the bitmap. No image resampling is needed.
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const m = nodeMatrix(target);
    matrix = [
      m[0] / canvas.width,
      m[1] / canvas.width,
      m[2] / canvas.height,
      m[3] / canvas.height,
      m[4] - (m[0] + m[2]) / 2,
      m[5] - (m[1] + m[3]) / 2,
    ];
    checkSize(canvas.width, canvas.height);
    canvas.getContext("2d")!.drawImage(image, 0, 0);
  } else {
    const b = effectBounds(target),
      pad = feather * 3,
      x = Math.floor(b.x - pad),
      y = Math.floor(b.y - pad),
      width = Math.ceil(b.x + b.width + pad) - x,
      height = Math.ceil(b.y + b.height + pad) - y;
    // Highest source density along any affine direction. An image compressed
    // non-uniformly must retain its most detailed axis, too.
    let density = 4;
    if (image) {
      const m = nodeMatrix(target),
        inv = invertAffine([
          m[0] / image.naturalWidth,
          m[1] / image.naturalWidth,
          m[2] / image.naturalHeight,
          m[3] / image.naturalHeight,
          0,
          0,
        ])!,
        a = inv[0] ** 2 + inv[1] ** 2,
        b = inv[2] ** 2 + inv[3] ** 2,
        c = inv[0] * inv[2] + inv[1] * inv[3];
      density = Math.max(1, Math.sqrt((a + b + Math.hypot(a - b, 2 * c)) / 2));
    } else {
      density = Math.min(
        density,
        16384 / width,
        16384 / height,
        Math.sqrt(32_000_000 / (width * height)),
      );
    }
    if (density < 1) throw new Error(sizeError);
    const w = Math.ceil(width * density),
      h = Math.ceil(height * density);
    checkSize(w, h);
    const renderer = new NativeCanvasRenderer(canvas, images);
    dispose = () => renderer.dispose();
    try {
      renderer.draw(
        {
          document,
          camera: { x: -x * density, y: -y * density, zoom: density },
          width: w,
          height: h,
          dpr: 1,
        },
        true,
        new Set([target.id]),
      );
    } catch (error) {
      renderer.dispose();
      throw error;
    }
    // The output owns the surface until the caller has extracted both parts.
    matrix = [1 / density, 0, 0, 1 / density, x, y];
    blurScale = density;
  }
  return { canvas, matrix, blurScale, exact: !!exact, dispose };
}

const sizeError =
  "Слой слишком велик для обработки области без потери качества. Уменьшите исходное изображение.";
function checkSize(width: number, height: number) {
  if (width * height > 32_000_000 || width > 16384 || height > 16384)
    throw new Error(sizeError);
}
